import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';
import log from '../../logger';

// Fetch users
export const fetchUsers = createAsyncThunk(
  'user/fetchUsers',
  async ({ page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Fetching users with page:', page, 'and limit:', limit, 'and filters:', filters);
      
      // Extract search term from filters
      const { search, ...otherFilters } = filters;
      const params = { page, limit, ...otherFilters };
      
      // Add search parameter if provided
      if (search) {
        params.search = search;
      }
      
      const response = await api.users.getAll(params);
      if (response.data.status) {
        log.info('Users fetched successfullyyyy:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch users:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching users:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching users');
    }
  }
);

// Create user
export const createUser = createAsyncThunk(
  'user/createUser',
  async (formData, { rejectWithValue }) => {
    try {
      log.info('Creating user with data:', formData);
      
      // Check if formData is FormData (has files) or regular object
      const isFormData = formData instanceof FormData;
      const response = await api.users.create(formData, isFormData);
      
      if (response.data.status) {
        log.info('User created successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to create user:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error creating user:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to create user');
    }
  }
);

// Update user
export const updateUser = createAsyncThunk(
  'user/updateUser',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      log.info('Updating user with ID:', id, 'and data:', formData);
      
      // Check if formData is FormData (has files) or regular object
      const isFormData = formData instanceof FormData;
      const response = await api.users.update(id, formData, isFormData);
      
      if (response.data.status) {
        log.info('User updated successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to update user:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error updating user:', error);
      return rejectWithValue(error.response?.data?.message || 'Error updating user');
    }
  }
);

// Delete user
export const deleteUser = createAsyncThunk(
  'user/deleteUser',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Deleting user with ID:', id);
      const response = await api.users.delete(id);
      if (response.data.status) {
        log.info('User deleted successfully:', id);
        return id;
      } else {
        log.warn('Failed to delete user:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error deleting user:', error);
      return rejectWithValue(error.response?.data?.message || 'Error deleting user');
    }
  }
);

// Bulk delete users
export const bulkDeleteUsers = createAsyncThunk(
  'user/bulkDeleteUsers',
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map(id => api.users.delete(id)));
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error deleting users');
    }
  }
);

// Bulk activate/deactivate users
export const bulkUpdateUserStatus = createAsyncThunk(
  'user/bulkUpdateStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      await api.users.bulkUpdateStatus(ids, status);
      return { ids, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error updating user status');
    }
  }
);

// Fetch a single user by ID
export const fetchUserById = createAsyncThunk(
  'user/fetchUserById',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Fetching user with ID:', id);
      const response = await api.users.getById(id);
      if (response.data.status) {
        log.info('User fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch user:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching user:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching user');
    }
  }
);

const initialState = {
  items: [],
  total: 0,
  page: 1,
  pages: 0,
  limit: 10,
  loading: false,
  error: null
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.users;
        state.total = parseInt(action.payload.total);
        state.page = parseInt(action.payload.page);
        state.pages = parseInt(action.payload.pages);
        state.limit = parseInt(action.payload.limit);
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create user
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items.push(action.payload);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update user
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = state.items.filter(item => item._id !== action.payload);
      })
      .addCase(deleteUser.rejected, (state) => {
        state.loading = false;
      })
      // Bulk delete users
      .addCase(bulkDeleteUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteUsers.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteUsers.rejected, (state) => {
        state.loading = false;
      })
      .addCase(bulkUpdateUserStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateUserStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { ids, status } = action.payload;
        state.items = state.items.map(item => ids.includes(item._id) ? { ...item, status } : item);
      })
      .addCase(bulkUpdateUserStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch a single user by ID
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = [action.payload]; // Assuming we want to replace the current items with the fetched one
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default userSlice.reducer;